/**
 * GettingStartedLane — onboarding path shown on the dashboard for zero-data
 * accounts (CLARID-UX-2026-06, PR 5). Fixes audit finding F4 (the dashboard is
 * a status board, not a launchpad): a brand-new account lands on a clear,
 * ordered setup path instead of a wall of empty widgets.
 *
 * Rendered only while the team has no compliance health score yet (no data).
 */

import { Link } from 'react-router-dom';
import {
  BuildingLibraryIcon,
  TableCellsIcon,
  MegaphoneIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';

const STEPS = [
  {
    n: 1,
    title: 'Set up your bank',
    desc: 'Add your institution details, assessment areas, and branches.',
    to: '/organization',
    icon: BuildingLibraryIcon,
  },
  {
    n: 2,
    title: 'Upload your HMDA LAR',
    desc: 'Validate your loan/application register against the FFIEC edits.',
    to: '/hmda/lar-validation',
    icon: TableCellsIcon,
  },
  {
    n: 3,
    title: 'Review marketing content',
    desc: 'Screen an ad or your website for advertising & UDAAP issues.',
    to: '/upload',
    icon: MegaphoneIcon,
  },
  {
    n: 4,
    title: 'Run your first analysis',
    desc: 'Generate Fair Lending and CRA findings from your data.',
    to: '/fair-lending',
    icon: ScaleIcon,
  },
];

export default function GettingStartedLane() {
  return (
    <div className="rounded-lg border border-teal-100 bg-gradient-to-br from-teal-50/80 to-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-navy-900">Getting started</h2>
      <p className="mt-1 text-sm text-gray-600">
        A few steps to your first compliance results — your health score and dashboard fill in as you
        add data.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {STEPS.map((step) => (
          <Link
            key={step.n}
            to={step.to}
            className="group flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-teal-300 hover:bg-teal-50/40"
          >
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-teal-100 text-sm font-semibold text-teal-700">
              {step.n}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                <step.icon className="h-4 w-4 text-teal-600" />
                {step.title}
              </div>
              <p className="mt-0.5 text-xs text-gray-500">{step.desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
